import { type FC } from "react";
import { Toolbar, Checkbox, Button, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import { useTranslation } from "react-i18next";
import {
  useDeleteCountryAccessesMutation,
  type CountryAccessesItem,
} from "@/partner/entities/countryAccesses";

interface CountryAccessesListToolbarProps {
  indeterminate: boolean;
  numSelected: number;
  nodes: CountryAccessesItem[];
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedItems: { id: string }[];
  onCloseSelected: () => void;
}

export const CountryAccessesListToolbar: FC<
  CountryAccessesListToolbarProps
> = ({
  indeterminate,
  numSelected,
  nodes,
  onSelectAllClick,
  selectedItems,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();

  const [deleteCountryAccesses, { loading: deleteLoading }] =
    useDeleteCountryAccessesMutation({
      onCompleted: () => {
        onCloseSelected();
        enqueueSnackbar(t("countryAccesses.toolbar.deleteSuccess"), {
          variant: "success",
        });
      },
      onError: (error: { message: string }) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["CountryAccessesList", "CountryAccessesSelect"],
    });

  const onDeleteSelected = () => {
    // До этой правки массовое удаление здесь шло вообще без вопроса.
    confirm({
      title: t("common.actions.delete"),
      text: t("common.toolbar.deleteConfirm"),
      onConfirm: () =>
        deleteCountryAccesses({
          variables: {
            ids: selectedItems.map((item) => item.id),
          },
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={numSelected === nodes.length}
        onChange={onSelectAllClick}
        sx={{
          marginLeft: "-16px",
          marginRight: {
            xs: 0,
            sm: "16px",
          },
        }}
      />

      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>

      <Divider
        orientation="vertical"
        variant="middle"
        sx={{
          mx: 1.25,
          height: "2em",
        }}
      />

      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={deleteLoading || numSelected === 0}
      >
        {t("common.actions.delete")}
      </Button>

      <ToolbarProgress loading={deleteLoading} />
      {confirmDialog}
    </Toolbar>
  );
};
